import { useEffect, useState } from 'react';
import { SCUIContent } from '../../../atomic/organisms';
import {
    Select,
    MenuItem,
    Checkbox,
    FormControl,
    InputLabel,
    Autocomplete,
    TextField,
    Box,
    Typography,
    Paper,
    Divider,
    Tooltip,
    InputAdornment,
    Chip,
    CircularProgress,
    Fade,
} from '@mui/material';
import { ReturnQuery } from '../../../services/query';
import { ReturnCommand } from '../../../services/command';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../atomic/atoms';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import ChecklistIcon from '@mui/icons-material/Checklist';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const inputSx = {
    fontSize: 13,
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        fontSize: 13,
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontSize: 13 },
};

function ReturnStatusManagement() {
    const { t } = useTranslation();
    const [selectedValue, setSelectedValue] = useState('');
    const [selectedOption, setSelectedOption] = useState<any>(null);
    const [returnFromStatus, setReturnFromStatus] = useState<any>();
    const [targetStatuses, setTargetStatuses] = useState<any>();
    const [rolesList, setRolesList] = useState<any>();
    const [selectedTargetValue, setSelectedTargetValue] = useState('');
    const [loadingTargets, setLoadingTargets] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);

    useEffect(() => {
        ReturnQuery.GetReturnStatus().then((res: any) => {
            setReturnFromStatus(res.data);
        });
    }, []);

    const handleTargetSelectChange = (event: any) => {
        setRolesList([]);
        setSelectedTargetValue(event.target.value);
    };
    useEffect(() => {
        if (selectedTargetValue) {
            getRolesList();
        }
    }, [selectedTargetValue]);

    const handleSelectChange = async (event: any, newValue: any) => {
        setTargetStatuses([]);
        setRolesList([]);
        setSelectedTargetValue('');
        setSelectedOption(newValue);
        const statusId = newValue ? newValue.returnStatusId : '';
        setSelectedValue(statusId);

        if (statusId) {
            setLoadingTargets(true);
            let req = { FromReturnStatusId: statusId };
            ReturnCommand.GetReturnStatusChangeMatrixList(req)
                .then((res: any) => {
                    if (res) {
                        setTargetStatuses(res);
                    } else {
                        setTargetStatuses([]);
                    }
                })
                .finally(() => setLoadingTargets(false));
        }
    };
    const handleRoleChange = (event: any) => {
        const { name, checked } = event.target;
        if (checked) {
            rolesList.find((role: any) => role.roleNameKey === name).isChecked = true;
        } else {
            rolesList.find((role: any) => role.roleNameKey === name).isChecked = false;
        }
        setRolesList([...rolesList]);
    };
    const getRolesList = () => {
        setRolesList([]);
        setLoadingRoles(true);
        ReturnCommand.GetAllOperatorsWithSelectedStatus({
            fromReturnStatusId: selectedValue,
            toStatusId: selectedTargetValue,
        })
            .then((res: any) => {
                if (res && res.length > 0) {
                    setRolesList(res);
                } else {
                    setRolesList([]);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            })
            .finally(() => setLoadingRoles(false));
    };
    const handleSubmit = () => {
        let selectedRoles = rolesList.filter((role: any) => role.isChecked === true).map((role: any) => role.roleId);
        let req = { RoleIds: selectedRoles, fromReturnStatusId: selectedValue, toStatusId: selectedTargetValue };
        ReturnCommand.AddOrUpdateReturnStatusForManagement(req).then(() => {
            Swal.fire(t('general.added'), ' ', 'success');
        });
    };

    const selectedCount = rolesList ? rolesList.filter((r: any) => r.isChecked).length : 0;

    return (
        <SCUIContent>
            <Box sx={{ maxWidth: 680 }}>
                {/* Header + Save */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2.5,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AssignmentReturnIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                        <Typography fontWeight={800} fontSize={17} color="text.primary" letterSpacing={0.2}>
                            {t('shippingListStatusManagement.pageTitle')}
                        </Typography>
                    </Box>
                    <SCUIButton
                        onClick={handleSubmit}
                        type="primary"
                        sizeType="fixed"
                        size="s"
                        text={t('general.save')}
                        showStartIcon
                        startIcon={<SaveIcon style={{ fontSize: '15px' }} />}
                        style={{
                            height: '34px',
                            padding: '0 20px',
                            borderRadius: '8px',
                            fontWeight: 600,
                            fontSize: '12px',
                        }}
                    />
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        p: 3,
                        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Source Status */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <LocalShippingIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                                <Typography fontWeight={700} fontSize={13} color="text.primary">
                                    {t('general.sourceRole')}
                                </Typography>
                            </Box>
                            {returnFromStatus && (
                                <Autocomplete
                                    options={returnFromStatus}
                                    getOptionLabel={(option: any) => option.statusDescription || ''}
                                    value={selectedOption}
                                    onChange={handleSelectChange}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('general.selection')}
                                            variant="outlined"
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start">
                                                                <LocalShippingIcon
                                                                    sx={{ fontSize: 17, color: 'grey.400' }}
                                                                />
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                },
                                            }}
                                            sx={inputSx}
                                        />
                                    )}
                                    isOptionEqualToValue={(option: any, value: any) =>
                                        option.returnStatusId === value?.returnStatusId
                                    }
                                    slotProps={{
                                        listbox: {
                                            style: { maxHeight: 48 * 4.5 + 8 },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: '0 4px 0 5px' },
                                    }}
                                />
                            )}
                        </Box>

                        {/* Arrow + Target Status */}
                        {(loadingTargets || (targetStatuses && targetStatuses.length > 0)) && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Divider sx={{ flex: 1 }} />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 34,
                                            height: 34,
                                            borderRadius: '50%',
                                            backgroundColor: loadingTargets ? 'grey.200' : 'primary.main',
                                            color: loadingTargets ? 'grey.400' : 'white',
                                            boxShadow: loadingTargets ? 'none' : '0 2px 8px rgba(25,118,210,0.35)',
                                            flexShrink: 0,
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        {loadingTargets ? (
                                            <CircularProgress size={18} thickness={4} sx={{ color: 'primary.main' }} />
                                        ) : (
                                            <ArrowForwardIcon sx={{ fontSize: 17 }} />
                                        )}
                                    </Box>
                                    <Divider sx={{ flex: 1 }} />
                                </Box>

                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <CheckCircleOutlineIcon
                                            sx={{
                                                fontSize: 17,
                                                color: loadingTargets ? 'grey.400' : 'success.main',
                                                transition: 'color 0.3s',
                                            }}
                                        />
                                        <Typography
                                            fontWeight={700}
                                            fontSize={13}
                                            color={loadingTargets ? 'text.disabled' : 'text.primary'}
                                        >
                                            {t('shippingListStatusManagement.status') || 'Statü'}
                                        </Typography>
                                    </Box>
                                    {loadingTargets ? (
                                        <Box
                                            sx={{
                                                height: 40,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'grey.200',
                                                backgroundColor: 'grey.50',
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: 2,
                                                gap: 1.5,
                                            }}
                                        >
                                            <CircularProgress size={14} thickness={4} sx={{ color: 'grey.400' }} />
                                            <Typography fontSize={12} color="text.disabled">
                                                Yükleniyor...
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Fade in timeout={300}>
                                            <FormControl fullWidth size="small" sx={inputSx}>
                                                <InputLabel id="target-select-label">
                                                    {t('shippingListStatusManagement.status') || 'Statü'}
                                                </InputLabel>
                                                <Select
                                                    labelId="target-select-label"
                                                    value={selectedTargetValue}
                                                    label="Statü"
                                                    onChange={handleTargetSelectChange}
                                                    displayEmpty
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 48 * 4.5 + 8, width: 250 },
                                                        },
                                                    }}
                                                >
                                                    {targetStatuses.map((item: any, index: any) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={item.toStatusId}
                                                            sx={{ fontSize: 13 }}
                                                        >
                                                            {item.toStatusIdDescription}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Fade>
                                    )}
                                </Box>
                            </>
                        )}

                        {/* Roles */}
                        {(loadingRoles || (rolesList && targetStatuses && targetStatuses.length > 0)) && (
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1.5,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ManageAccountsIcon
                                            sx={{
                                                fontSize: 17,
                                                color: loadingRoles ? 'grey.400' : 'primary.main',
                                                transition: 'color 0.3s',
                                            }}
                                        />
                                        <Typography
                                            fontWeight={700}
                                            fontSize={13}
                                            color={loadingRoles ? 'text.disabled' : 'text.primary'}
                                        >
                                            {t('shippingListStatusManagement.roles')}
                                        </Typography>
                                        {loadingRoles && (
                                            <CircularProgress
                                                size={13}
                                                thickness={4}
                                                sx={{ color: 'grey.400', ml: 0.5 }}
                                            />
                                        )}
                                    </Box>
                                    {!loadingRoles && selectedCount > 0 && (
                                        <Chip
                                            icon={<ChecklistIcon />}
                                            label={`${selectedCount} seçili`}
                                            size="small"
                                            sx={{
                                                height: 24,
                                                fontSize: 11,
                                                fontWeight: 600,
                                                backgroundColor: 'rgba(25,118,210,0.08)',
                                                color: 'primary.main',
                                                border: '1px solid rgba(25,118,210,0.2)',
                                                '& .MuiChip-icon': { fontSize: 14, color: 'primary.main' },
                                            }}
                                        />
                                    )}
                                </Box>

                                {loadingRoles ? (
                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'grey.200',
                                            borderRadius: 2,
                                            height: 80,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1.5,
                                            backgroundColor: 'grey.50',
                                        }}
                                    >
                                        <CircularProgress size={18} thickness={4} sx={{ color: 'grey.400' }} />
                                        <Typography fontSize={12} color="text.disabled">
                                            Yükleniyor...
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Fade in timeout={300}>
                                        <Box
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'grey.200',
                                                borderRadius: 2,
                                                maxHeight: 230,
                                                overflowY: 'auto',
                                                '&::-webkit-scrollbar': { width: 4 },
                                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                                '&::-webkit-scrollbar-thumb': {
                                                    background: '#ccc',
                                                    borderRadius: 2,
                                                },
                                            }}
                                        >
                                            {rolesList.map((role: any, index: any) => (
                                                <Box
                                                    key={index}
                                                    onClick={() => {
                                                        const updated = [...rolesList];
                                                        updated[index].isChecked = !updated[index].isChecked;
                                                        setRolesList(updated);
                                                    }}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        py: 0.75,
                                                        cursor: 'pointer',
                                                        borderBottom:
                                                            index < rolesList.length - 1 ? '1px solid' : 'none',
                                                        borderBottomColor: 'grey.100',
                                                        transition: 'background-color 0.15s ease',
                                                        backgroundColor: role.isChecked
                                                            ? 'rgba(25,118,210,0.05)'
                                                            : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: role.isChecked
                                                                ? 'rgba(25,118,210,0.08)'
                                                                : 'rgba(0,0,0,0.025)',
                                                        },
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={role.isChecked || false}
                                                        onChange={handleRoleChange}
                                                        name={role.roleNameKey}
                                                        onClick={(e) => e.stopPropagation()}
                                                        size="small"
                                                        icon={
                                                            <RadioButtonUncheckedIcon
                                                                sx={{ fontSize: 18, color: 'grey.400' }}
                                                            />
                                                        }
                                                        checkedIcon={
                                                            <CheckCircleOutlineIcon
                                                                sx={{ fontSize: 18, color: 'primary.main' }}
                                                            />
                                                        }
                                                        sx={{ p: 0.5, mr: 1.5 }}
                                                    />
                                                    <Tooltip title={role.roleNameKey} placement="right" arrow>
                                                        <Typography
                                                            fontSize={13}
                                                            fontWeight={role.isChecked ? 700 : 400}
                                                            color={role.isChecked ? 'primary.main' : 'text.primary'}
                                                            noWrap
                                                            onClick={() => {
                                                                const updated = [...rolesList];
                                                                updated[index].isChecked = !updated[index].isChecked;
                                                                setRolesList(updated);
                                                            }}
                                                            sx={{ userSelect: 'none', maxWidth: 460 }}
                                                        >
                                                            {role.roleNameKey}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Fade>
                                )}
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </SCUIContent>
    );
}

export default ReturnStatusManagement;
